import { defineStore } from 'pinia';
import { getData } from '@/services/axios-service';
// import { useAuthStore } from './auth-store';
import {
  ElasticState,
  RentalUnit,
  PropertyElements,
  Property,
} from '@/interfaces/elastic/elastic.interface';
import { PaginationResponse } from '@/interfaces/meta.interface';
import { BASE_URL } from '@/utils/constants/api-constants';

const INITIAL_STATE: ElasticState = {
  pagination: {
    count: 0,
    limit: 16,
    page: 1,
    pages: 0,
  },
  selectedFilters: {
    filterItems: [],
    mappedFilters: {},
  },
  rentalUnits: [],
  properties: [],
  rentalUnitsInPage: [],
  propertyInPage: [],
  propertyTypeOptions: [],
  isLoading: false,
  propertySelected: undefined,
  filters: {
    minPrice: undefined,
    maxPrice: undefined,
    lon: undefined,
    lat: undefined,
    PropertyTypeId: -1,
    city: '',
    countryCode: '',
    country: '',
    startAvailabilityDate: '',
    endAvailabilityDate: '',
  },
};

export const useElasticStore = defineStore('ElasticSearchStore', {
  state: (): ElasticState => ({
    ...INITIAL_STATE,
  }),
  actions: {
    resetStore() {
      this.$reset();
    },
    async getProperties(page: number = 1) {
      try {
        if (page) this.pagination.page = page;

        const { data, success, error } = await getData<PaginationResponse<Property[]>>(
          `${BASE_URL.ELASTIC}v1/public/property?isActive=true&isPublished=true`,
          {
            limit: this.pagination.limit,
            page: this.pagination.page,
            ...this.selectedFilters.mappedFilters,
          },
        );

        if (success && data) {
          const { rows, count, limit, pages, page } = data;
          this.pagination.count = count;
          this.pagination.limit = limit;
          this.pagination.page = page;
          this.pagination.pages = pages;
          this.properties = rows;
          return rows;
        } else {
          new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getPropertyPreview(propertyPublicId: string) {
      try {
        const { data, success, error } = await getData<Property>(
          `${BASE_URL.ELASTIC}v1/public/property/${propertyPublicId}`,
        );

        if (success && data) {
          return data;
        } else {
          new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getRentalUnits(page: number = 1) {
      try {
        if (page) this.pagination.page = page;

        const { data, success, error } = await getData<PaginationResponse<RentalUnit[]>>(
          `${BASE_URL.ELASTIC}v1/public/rental-unit/search`,
          {
            limit: this.pagination.limit,
            page: this.pagination.page,
            ...this.selectedFilters.mappedFilters,
          },
        );

        if (success && data) {
          const { rows, count, limit, pages, page } = data;

          this.pagination.count = count;
          this.pagination.limit = limit;
          this.pagination.page = page;
          this.pagination.pages = pages;

          this.rentalUnits = rows;
          return rows;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getRentalUnitPreview(id: string) {
      try {
        // const { user } = useAuthStore();
        const queryParams = new URLSearchParams(window.location.search);
        const queryString = queryParams.toString();

        const { data, success, error } = await getData<RentalUnit>(
          `${BASE_URL.ELASTIC}v1/public/rental-unit/${id}?ignoreRedirect=true&${queryString}`,
        );

        if (success && data) {
          return data;
        } else {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? 'Unknown error!');
      }
    },
    async getPropertyElements() {
      try {
        const response = await getData<PropertyElements>(
          BASE_URL.RENTALS + 'public/properties/static-elements',
        );
        const { success, data, error } = response;
        if (success && data) {
          const { propertiesTypes } = data;
          this.propertyTypeOptions = propertiesTypes;
          return response;
        } else {
          throw new Error(error);
        }
      } catch (error: unknown) {
        const errorMsg = error?.toString() ?? 'Unknown error!';
        throw new Error(errorMsg);
      }
    },

    addOrClearFilter(key: string, isValid: boolean, value: string | number | boolean) {
      if (isValid) {
        this.selectedFilters.mappedFilters[`${key}`] = value;
      } else if (`${key}` in this.selectedFilters.mappedFilters) {
        delete this.selectedFilters.mappedFilters[`${key}`];
      }
    },
  },
});
